<template>
  <span class="sell-button-wrapper">
    <button
      v-if="hasShares"
      type="button"
      :class="['btn', 'btn-sm', isShort ? 'btn-primary' : 'btn-danger']"
      @click="onActionClick"
    >
      {{ buttonLabel }}
    </button>

    <span v-if="successMessage" class="ms-2 text-success small fw-semibold">
      {{ successMessage }}
    </span>

    <SellConfirmationModal
      :visible="modalVisible"
      :ticker="ticker"
      :quantity="absoluteQuantity"
      :currentPrice="currentPrice"
      :side="side"
      :errorMessage="errorMessage"
      @confirm="onConfirm"
      @cancel="onCancel"
    />
  </span>
</template>

<script>
import SellConfirmationModal from './SellConfirmationModal.vue';
import { placeOrder } from '../services/api';

export default {
  name: 'SellButton',

  components: { SellConfirmationModal },

  props: {
    ticker: {
      type: String,
      required: true,
    },
    quantity: {
      type: Number,
      required: true,
    },
    currentPrice: {
      type: Number,
      required: true,
    },
    side: {
      type: String,
      default: 'LONG',
      validator: (v) => ['LONG', 'SHORT'].includes((v || '').toUpperCase()),
    },
  },

  emits: ['sold'],

  data() {
    return {
      modalVisible: false,
      errorMessage: '',
      successMessage: '',
      successTimer: null,
    };
  },

  computed: {
    isShort() {
      return (this.side || '').toUpperCase() === 'SHORT';
    },
    absoluteQuantity() {
      return Math.abs(this.quantity);
    },
    hasShares() {
      return this.absoluteQuantity > 0;
    },
    buttonLabel() {
      return this.isShort ? 'Buy' : 'Sell';
    },
    orderSide() {
      return this.isShort ? 'buy' : 'sell';
    },
  },

  beforeUnmount() {
    if (this.successTimer) clearTimeout(this.successTimer);
  },

  methods: {
    onActionClick() {
      console.debug(`[SellButton] ${this.buttonLabel} button clicked for ticker: ${this.ticker}`);
      this.errorMessage = '';
      this.successMessage = '';
      this.modalVisible = true;
    },

    onCancel() {
      console.debug(`[SellButton] modal cancelled for ticker: ${this.ticker}`);
      this.modalVisible = false;
      this.errorMessage = '';
    },

    async onConfirm({ ticker, qty }) {
      const side = this.orderSide;
      console.debug(`[SellButton] modal confirmed — ticker: ${ticker}, side: ${side}, qty: ${qty}`);
      try {
        const payload = { ticker, side, qty };
        if (this.isShort) {
          payload.position_side = 'SHORT';
          payload.intent = 'SHORT_COVER';
        }
        await placeOrder(payload.ticker, payload.side, payload.qty, {
          position_side: payload.position_side,
          intent: payload.intent,
        });
        console.debug(`[SellButton] API success — ${side} order placed for ${ticker}`);
        this.modalVisible = false;
        this.errorMessage = '';
        this.$emit('sold', { ticker, qty, side });
        const verb = this.isShort ? 'Covered' : 'Sold';
        this.successMessage = `${verb} ${qty} share${qty !== 1 ? 's' : ''} of ${ticker}`;
        if (this.successTimer) clearTimeout(this.successTimer);
        this.successTimer = setTimeout(() => {
          this.successMessage = '';
        }, 4000);
      } catch (err) {
        const verb = this.isShort ? 'Buy-to-cover' : 'Sell';
        console.error(`[SellButton] API error — ${side} order failed for ${ticker}:`, err);
        this.errorMessage =
          err?.response?.data?.detail ||
          err?.message ||
          `${verb} order failed. Please try again.`;
      }
    },
  },
};
</script>
